import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Loader2, Sparkles, Edit3 } from 'lucide-react';

export const GenerationState = ({
  generating, generatingProgress, error, generationBgImage, tripInput,
  onModifyPlan, onGenerate,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white/90 backdrop-blur-sm rounded-xl border border-sky/20 p-12 text-center shadow-md"
    data-testid="generation-state"
  >
    {generating ? (
      <div className="relative overflow-hidden rounded-xl -m-12">
        {generationBgImage && (
          <img src={generationBgImage} alt={tripInput.destination_city} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className={`relative z-10 p-12 ${generationBgImage ? 'bg-ocean/70 backdrop-blur-sm' : ''}`}>
          <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 relative backdrop-blur-md">
            <Loader2 className={`w-12 h-12 animate-spin ${generationBgImage ? 'text-white' : 'text-ocean'}`} />
            <div className={`absolute inset-0 rounded-full border-4 animate-pulse ${generationBgImage ? 'border-white/30' : 'border-ocean/20'}`} />
          </div>
          <h2 className={`font-serif text-2xl mb-3 ${generationBgImage ? 'text-white' : 'text-charcoal'}`}>
            Generating Your Itinerary
          </h2>
          <p className={`font-medium mb-2 ${generationBgImage ? 'text-gold' : 'text-ocean'}`}>
            {generatingProgress}
          </p>
          <p className={`max-w-md mx-auto ${generationBgImage ? 'text-white/80' : 'text-charcoal/60'}`}>
            Our AI is crafting a personalized travel plan for {tripInput.destination_city}.
            This may take up to 2 minutes...
          </p>
          <div className="mt-6 w-64 h-2 bg-white/20 rounded-full mx-auto overflow-hidden">
            <div className={`h-full rounded-full animate-pulse ${generationBgImage ? 'bg-gold' : 'bg-ocean'}`} style={{ width: '60%' }} />
          </div>
        </div>
      </div>
    ) : error ? (
      <>
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="font-serif text-2xl text-charcoal mb-3">Generation Failed</h2>
        <p className="text-red-600 mb-4 max-w-md mx-auto">{error}</p>
        <p className="text-charcoal/60 mb-8 max-w-md mx-auto">Please try again. If the problem persists, try modifying your trip details.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button className="bg-ocean hover:bg-ocean-light text-white px-8 py-6 rounded-full font-sans font-medium transition-all duration-200 hover:shadow-lg active:scale-95 text-lg" onClick={onModifyPlan}>
            <Edit3 className="w-5 h-5 mr-2" /> Modify Plan
          </Button>
          <Button className="bg-gold hover:bg-gold-light text-charcoal px-8 py-6 rounded-full font-sans font-medium transition-all duration-200 hover:shadow-lg active:scale-95 text-lg" onClick={onGenerate}>
            <Sparkles className="w-5 h-5 mr-2" /> Try Again
          </Button>
        </div>
      </>
    ) : (
      <>
        <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Sparkles className="w-10 h-10 text-gold" />
        </div>
        <h2 className="font-serif text-2xl text-charcoal mb-3">Ready to Generate Your Itinerary</h2>
        <p className="text-charcoal/60 mb-8 max-w-md mx-auto">
          Click the button below to let our AI create a personalized day-by-day travel plan based on your preferences.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button className="bg-ocean hover:bg-ocean-light text-white px-8 py-6 rounded-full font-sans font-medium transition-all duration-200 hover:shadow-lg active:scale-95 text-lg" onClick={onModifyPlan} data-testid="modify-plan-cta-btn">
            <Edit3 className="w-5 h-5 mr-2" /> Modify Plan
          </Button>
          <Button className="bg-gold hover:bg-gold-light text-charcoal px-8 py-6 rounded-full font-sans font-medium transition-all duration-200 hover:shadow-lg active:scale-95 text-lg" onClick={onGenerate} data-testid="generate-cta-btn">
            <Sparkles className="w-5 h-5 mr-2" /> Generate Itinerary
          </Button>
        </div>
      </>
    )}
  </motion.div>
);
